import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCoreRowModel } from '@tanstack/react-table';

import { Paper } from '@mui/material';

import { Storage } from '../../../utils/Storage';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { appUserPasswordResetRequest, exportAppUsers, getAppUsers, unlockAppUser } from '../../../services/user.service';

import { Table, useStandardTable as useTable } from '../../common/table';
import SearchBar from '../../common/SearchBar/SearchBar';
import CustomModal from '../../common/withModal';

import { SearchBarFilterTypes } from '../../../constants/Constants';

import UserScopeView from '../authorization/appuser/userScopeView';

import makeAppUserColumn from './makeAppUserColumn';

export default function AppUsers() {
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [{ lastName: '1' }],
  });

  const [scopePopupOpen, setScopePopupOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const handleOpenScopePopup = (id: string) => {
    setSelectedUserId(id);
    setScopePopupOpen(true);
  };

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.USERS);

    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
        sortBy: paramFilter.sortBy,
      });
    }
  };

  useEffect(() => {
    intialDefaultFilters();
  }, []);

  const notifications = useNotifyMessage();
  const navigateTo = useNavigate();
  const resetPassword = async (id: string) => {
    const response = await appUserPasswordResetRequest(id);

    if (response.status === 200) {
      notifications.notifyMessage('Password reset code has been sent', 'success');

      return;
    }

    notifications.notifyMessage(response.data, 'error');
  };

  const manageUserPermissions = (id: string) => {
    navigateTo(`/users/${id}`);
  };

  const unLock = async (id: string) => {
    const response = await unlockAppUser(id);

    if (response.status === 200) {
      notifications.notifyMessage('User unlocked', 'success');
      setParamFilter({ ...paramFilter, changedAt: new Date() });

      return;
    }

    notifications.notifyMessage('FAILED to unlock user', 'error');
  };

  const columns = useMemo(() => makeAppUserColumn(resetPassword, manageUserPermissions, unLock, handleOpenScopePopup), []);
  const table = useTable<any>({
    columns,
    accessKey: 'id',
    fetchAsync: getAppUsers,
    exportAsync: exportAppUsers,
    params: paramFilter,
    serverSide: true,
  });
  const onSearchChange = (filters: any) => {
    setParamFilter({
      filters: filters,
      sortBy: paramFilter.sortBy,
    });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.USERS, { isMoreFilter: true, filters: filters });
  };

  return (
    <>
      <h1>Users</h1>
      <>
        <SearchBar pageKey={SearchBarFilterTypes.USERS} onSearch={onSearchChange} columns={columns} allowSearchAll={true} />
        <Paper>
          <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
        </Paper>
        <CustomModal title='User Scope' isOpen={scopePopupOpen} setIsOpen={setScopePopupOpen} showSaveButton={false} maxWidth='md'>
          {selectedUserId && <UserScopeView userId={selectedUserId} />}
        </CustomModal>
      </>
    </>
  );
}
